import { action, computed, makeObservable, observable } from 'mobx';
import type { History, Location as HistoryLocation } from 'history';

export interface LocationState {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export class RouterStore {
  @observable
  location: LocationState = {
    pathname: '',
    search: '',
    hash: '',
    state: null,
    key: '',
  };

  @computed
  get pathname(): string {
    return this.location.pathname;
  }

  @computed
  get search(): string {
    return this.location.search;
  }

  @computed
  get hash(): string {
    return this.location.hash;
  }

  @action
  updateLocation(location: LocationState): void {
    this.location = location;
  }

  @action
  push(path: string, state?: unknown): void {
    this.pendingNavigation = { type: 'push', path, state };
  }

  @action
  replace(path: string, state?: unknown): void {
    this.pendingNavigation = { type: 'replace', path, state };
  }

  @action
  go(n: number): void {
    this.pendingNavigation = { type: 'go', n };
  }

  @action
  goBack(): void {
    this.go(-1);
  }

  @action
  goForward(): void {
    this.go(1);
  }

  @observable
  pendingNavigation:
    | { type: 'push'; path: string; state?: unknown }
    | { type: 'replace'; path: string; state?: unknown }
    | { type: 'go'; n: number }
    | null = null;

  @action
  clearPendingNavigation(): void {
    this.pendingNavigation = null;
  }

  history: History | null = null;

  private unsubscribeHistory: (() => void) | null = null;

  @action
  private syncLocation(location: HistoryLocation): void {
    this.location = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
      key: location.key || '',
    };
  }

  @action
  executePendingNavigation(): void {
    if (!this.pendingNavigation || !this.history) {
      return;
    }

    const nav = this.pendingNavigation;
    this.pendingNavigation = null;

    switch (nav.type) {
      case 'push':
        this.history.push(nav.path, nav.state);
        break;
      case 'replace':
        this.history.replace(nav.path, nav.state);
        break;
      case 'go':
        this.history.go(nav.n);
        break;
    }
  }

  init(history: History): void {
    if (this.history) {
      return;
    }

    this.history = history;
    this.syncLocation(history.location);

    this.unsubscribeHistory = history.listen(({ location }) => {
      this.syncLocation(location);
    });
  }

  destroy(): void {
    if (this.unsubscribeHistory) {
      this.unsubscribeHistory();
      this.unsubscribeHistory = null;
    }
    this.history = null;
  }

  constructor() {
    makeObservable(this);
  }
}
